import { useState } from "react";
import { toast } from "sonner";
import {
  ALLOWED_ATTACHMENT_MIME_TYPES,
  MAX_ATTACHMENT_BYTES,
  MAX_GENERIC_SINGLE_SHOT_BYTES,
  MAX_MULTIPART_BYTES,
  MULTIPART_PART_SIZE_BYTES,
  type FileKind,
  type FileRequestUploadInput,
} from "@bookdental/schemas";
import { trpc } from "@/lib/trpc";

type UploadState = "uploading" | "done" | "error";

/** STL/PLY de scanner intraoral quase sempre chegam com `file.type` vazio (tipo sem registro MIME
 *  oficial) — sem isso, o PUT presignado do R2 recebe Content-Type "" e a allowlist recusa, mesmo
 *  o arquivo sendo válido. "application/octet-stream" é o fallback genérico já aceito pro Drive. */
function tipoNormalizado(file: File): string {
  return file.type || "application/octet-stream";
}

/** Lógica de upload (3 passos por arquivo: requestUpload → PUT direto → complete, com fallback
 *  multipart pra arquivo grande) extraída de FilesDropzone pra ser reaproveitada tanto pelo botão
 *  compacto (FilesUploadButton) quanto por um drop direto na interface de diretório. */
export function useFileUpload({
  kind,
  onUploaded,
}: {
  kind: FileKind;
  onUploaded: (file: { fileId: string; name: string }) => void;
}) {
  const [progress, setProgress] = useState<Map<string, UploadState>>(new Map());
  const requestUpload = trpc.file.requestUpload.useMutation();
  const complete = trpc.file.complete.useMutation();
  const initMultipartUpload = trpc.file.initMultipartUpload.useMutation();
  const getMultipartPartUrl = trpc.file.getMultipartPartUrl.useMutation();
  const completeMultipartUpload = trpc.file.completeMultipartUpload.useMutation();
  const abortMultipartUpload = trpc.file.abortMultipartUpload.useMutation();

  const uploading = [...progress.values()].some((s) => s === "uploading");
  const total = progress.size;
  const doneCount = [...progress.values()].filter((s) => s !== "uploading").length;

  async function uploadSingleShot(file: File) {
    const contentType = tipoNormalizado(file) as FileRequestUploadInput["contentType"];
    const { fileId, uploadUrl, uploadHeaders } = await requestUpload.mutateAsync({
      kind,
      fileName: file.name,
      contentType,
      contentLength: file.size,
    } as FileRequestUploadInput);
    const res = await fetch(uploadUrl, { method: "PUT", headers: uploadHeaders, body: file });
    if (!res.ok) throw new Error(`"${file.name}": falha no upload.`);
    await complete.mutateAsync({ fileId });
    return fileId;
  }

  async function uploadMultipart(file: File) {
    const { fileId, uploadId } = await initMultipartUpload.mutateAsync({
      fileName: file.name,
      contentType: tipoNormalizado(file),
      contentLength: file.size,
    });

    const totalParts = Math.ceil(file.size / MULTIPART_PART_SIZE_BYTES);
    const parts: { partNumber: number; etag: string }[] = [];
    try {
      for (let i = 0; i < totalParts; i++) {
        const partNumber = i + 1;
        const start = i * MULTIPART_PART_SIZE_BYTES;
        const chunk = file.slice(start, start + MULTIPART_PART_SIZE_BYTES);
        const { url } = await getMultipartPartUrl.mutateAsync({ fileId, uploadId, partNumber });
        const res = await fetch(url, { method: "PUT", body: chunk });
        if (!res.ok) throw new Error(`"${file.name}": falha na parte ${partNumber}/${totalParts}.`);
        // Sem ExposeHeaders: ["ETag"] no CORS do bucket, o navegador esconde esse header
        // do JS. Inventar um valor aqui faria o upload seguir e morrer la' no complete,
        // com erro que nao aponta pra causa — melhor parar onde o problema esta'.
        const etag = res.headers.get("ETag") ?? res.headers.get("etag");
        if (!etag) {
          throw new Error(
            `"${file.name}": o servidor de arquivos nao expos o ETag da parte ${partNumber}. ` +
              `Verifique ExposeHeaders no CORS do bucket.`,
          );
        }
        parts.push({ partNumber, etag });
      }
      await completeMultipartUpload.mutateAsync({ fileId, uploadId, parts });
    } catch (err) {
      await abortMultipartUpload.mutateAsync({ fileId, uploadId }).catch(() => undefined);
      throw err;
    }
    return fileId;
  }

  async function handleFile(file: File) {
    const progressKey = `${file.name}-${file.size}-${crypto.randomUUID()}`;
    setProgress((prev) => new Map(prev).set(progressKey, "uploading"));

    try {
      if (kind === "PROSTHESIS_ATTACHMENT") {
        if (!(ALLOWED_ATTACHMENT_MIME_TYPES as readonly string[]).includes(tipoNormalizado(file))) {
          throw new Error(`"${file.name}": tipo não permitido (imagem, PDF, STL, PLY ou ZIP).`);
        }
        if (file.size > MAX_ATTACHMENT_BYTES) {
          throw new Error(`"${file.name}": arquivo muito grande (máx. ${MAX_ATTACHMENT_BYTES / (1024 * 1024)}MB).`);
        }
      } else if (file.size > MAX_MULTIPART_BYTES) {
        throw new Error(`"${file.name}": arquivo muito grande (máx. 500MB).`);
      }

      const fileId =
        kind === "GENERIC" && file.size > MAX_GENERIC_SINGLE_SHOT_BYTES
          ? await uploadMultipart(file)
          : await uploadSingleShot(file);

      setProgress((prev) => new Map(prev).set(progressKey, "done"));
      onUploaded({ fileId, name: file.name });
    } catch (err) {
      setProgress((prev) => new Map(prev).set(progressKey, "error"));
      toast.error(err instanceof Error ? err.message : "Não foi possível anexar o arquivo.");
    }
  }

  async function handleFiles(fileList: FileList | File[] | null) {
    if (!fileList || fileList.length === 0) return;
    setProgress(new Map());
    await Promise.allSettled(Array.from(fileList).map((file) => handleFile(file)));
  }

  return { uploading, total, doneCount, handleFiles };
}
